import React, { useEffect, useState, useRef } from "react";
import { useDialog } from "../../../contexts/DialogContext";
import { getTimeStamp } from "../../../utils/common";
import useAuth from "../../../hooks/useAuth";
import { useFormContext } from "react-hook-form";
import {
  ID_PROOF_TYPES,
  ADDRESS_PROOF_TYPES,
  PROFILE_PROOF_TYPES,
  CAF_PROOF_TYPES,
} from "../../../constants/kycDocumentTypes";

import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  Typography,
  Stack,
  Box,
  useMediaQuery,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useKycDocument from "../../../hooks/useKycDocument";
import { useSearchParams } from "react-router-dom";
import UploadKycDocument from "../../users/user_details/UploadKycDocument";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Approve from "../../users/user_details/Approve";
import RejectKycDocument from "../../users/user_details/RejectKycDocument";
import CustomTextField from "../../../components/CustomTextField";
import ReactHookFormSelect from "../../../components/ReactHookFormSelect";
import CustomFileField from "../../../components/CustomFileField";
import KycPreview from "./KycPreview";
import useCafForm from "../../../hooks/useCafForm";

function KycDocuments(props) {
  const { cafForm, getKycDocumentPreview } = useCafForm();
  const fileInputRef = useRef(null);
  const { t } = useTranslation();
  const {
    formState: { errors },
    control,
    watch,
    setValue,
  } = useFormContext();

  const { validationFields, readOnly = false } = props;

  let [searchParams] = useSearchParams();

  const { user } = useAuth();
  const { openDialog } = useDialog();
  const isMobile = useMediaQuery("(max-width:600px)");

  const kycDocumentId = searchParams.get("id");

  const currentPayload = {
    filters: [
      {
        fieldName: "radUserId",
        fieldValue: kycDocumentId,
        op: "eq",
        dataType: "int",
        condition: "and",
        isJoin: true,
        attributeName: "radUser",
        entityName: "RadUserEntity",
      },
    ],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
  };

  const { getAllKycDocument, kycDocuments, getKycDocumentImage } = useKycDocument();

  const [payload, setPayload] = useState(currentPayload);
  const [images, setImages] = useState({});

  useEffect(() => {
    if (kycDocumentId) {
      getAllKycDocument(payload);
    }
  }, [payload, kycDocumentId]);

  const watchedIdProof = watch("idProof");
  const watchedAddressProof = watch("addressProof");
  const watchedProfileProof = watch("profileProof");

  useEffect(() => {
    if (watchedIdProof && watchedIdProof instanceof File) {
      const url = URL.createObjectURL(watchedIdProof);
      setImages((prev) => ({ ...prev, idProof: url }));
      return () => URL.revokeObjectURL(url);
    }
  }, [watchedIdProof]);

  useEffect(() => {
    if (watchedAddressProof && watchedAddressProof instanceof File) {
      const url = URL.createObjectURL(watchedAddressProof);
      setImages((prev) => ({ ...prev, addressProof: url }));
      return () => URL.revokeObjectURL(url);
    }
  }, [watchedAddressProof]);

  useEffect(() => {
    if (watchedProfileProof && watchedProfileProof instanceof File) {
      const url = URL.createObjectURL(watchedProfileProof);
      setImages((prev) => ({ ...prev, profileProof: url }));
      return () => URL.revokeObjectURL(url);
    }
  }, [watchedProfileProof]);

  useEffect(() => {
    const loadImages = async () => {
      const map = {};
      try {
        if (cafForm.addressProofPath) {
          // Pass the full stored path so the server can locate the file on disk
          const addressProofUrl = await getKycDocumentPreview(
            cafForm.cafId,
            cafForm.addressProofPath,
          );
          map["addressProof"] = addressProofUrl;
        }

        if (cafForm.idProofPath) {
          const idProofUrl = await getKycDocumentPreview(cafForm.cafId, cafForm.idProofPath);
          map["idProof"] = idProofUrl;
        }

        if (cafForm.profileProofPath) {
          const profileProofUrl = await getKycDocumentPreview(
            cafForm.cafId,
            cafForm.profileProofPath,
          );
          map["profileProof"] = profileProofUrl;
        }
      } catch (error) {
        console.warn("Failed to load some KYC images", error);
      }

      setImages(map);
    };

    if (Object.keys(cafForm).length != 0) loadImages();
  }, [cafForm]);
  const hasNoDocuments = !kycDocuments || kycDocuments.length === 0;
  if (readOnly) {
    return (
      <Grid container spacing={6}>
        <Grid item xs={12} md={4}>
          <KycPreview
            title="Identity Proof"
            file={watchedIdProof}
            fileName={watchedIdProof?.name || cafForm?.idProofPath?.split("/").pop() || ""}
            previewUrl={images?.idProof}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <KycPreview
            title="Address Proof"
            file={watchedAddressProof}
            fileName={
              watchedAddressProof?.name || cafForm?.addressProofPath?.split("/").pop() || ""
            }
            previewUrl={images?.addressProof}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <KycPreview
            title="Profile Picture"
            file={watchedProfileProof}
            fileName={
              watchedProfileProof?.name || cafForm?.profileProofPath?.split("/").pop() || ""
            }
            previewUrl={images?.profileProof}
          />
        </Grid>
      </Grid>
    );
  }
  const uploadIdClick = (title) => {
    let dropDownValues = [];

    if (title === "Id Proof") {
      dropDownValues = ID_PROOF_TYPES.map((t) => ({ key: t.key, value: t.label }));
    }
    if (title === "Address Proof") {
      dropDownValues = ADDRESS_PROOF_TYPES.map((t) => ({ key: t.key, value: t.label }));
    }
    if (title === "Profile Proof") {
      dropDownValues = PROFILE_PROOF_TYPES.map((t) => ({ key: t.key, value: t.label }));
    }
    if (title === "CAF Proof") {
      dropDownValues = CAF_PROOF_TYPES.map((t) => ({ key: t.key, value: t.label }));
    }

    return openDialog(
      "upload_id_proof",
      "Upload Id Proof",
      <UploadKycDocument
        key={getTimeStamp()}
        radUserId={searchParams.get("id")}
        dropDownValues={dropDownValues}
        documentTitle={title}
        refreshPage={() => getAllKycDocument(payload)}
      />,
    );
  };

  const approveClick = (id) => {
    return openDialog(
      "approve",
      "Approve",
      <Approve
        key={getTimeStamp()}
        kycDocumentId={id}
        refreshPage={() => getAllKycDocument(payload)}
      />,
    );
  };

  const rejectClick = (id) => {
    return openDialog(
      "reject",
      "Reject KYC",
      <RejectKycDocument
        key={getTimeStamp()}
        kycDocumentId={id}
        refreshPage={() => getAllKycDocument(payload)}
      />,
    );
  };

  const approveOrRejectPermission = () => {
    if (!user) return false;
    return ["SUPER_ADMIN", "MULTI_SERVICE_OPERATOR"].includes(user.businessType);
  };

  const downloadKycDocument = (url, title) => {
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", title);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const isPending = (status) => status && status.trim().toLowerCase() === "pending";

  // ---------------------------------------------------------
  // ✅ DEFAULT SCREEN WHEN NO DOCUMENTS FOUND
  // ---------------------------------------------------------
  if (hasNoDocuments) {
    return (
      <Grid container spacing={3}>
        {/* LEFT SIDE – Upload & Inputs */}
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3, borderRadius: 3, height: "100%" }}>
            <CardHeader title="Upload KYC Documents" />
            <CardContent>
              <Grid container spacing={6}>
                <Grid item xs={12} md={4}>
                  <ReactHookFormSelect
                    name="identityProofType"
                    label={t("id_proof_type")}
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                  >
                    {ID_PROOF_TYPES.map((t) => (
                      <MenuItem key={t.key} value={t.key}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </ReactHookFormSelect>
                </Grid>
                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="ID Proof Number"
                    name="identityProofNo"
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                  />
                </Grid>

                {/* ID PROOF */}
                <Grid item xs={12} md={4}>
                  <CustomFileField
                    label={t("id_proof")}
                    name="idProof"
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                    ref={fileInputRef}
                    accept="application/pdf,image/*"
                  />
                </Grid>

                {/* ADDRESS PROOF */}
                <Grid item xs={12} md={4}>
                  <ReactHookFormSelect
                    name="addressProofType"
                    label={t("address_proof_type")}
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                  >
                    {ADDRESS_PROOF_TYPES.map((t) => (
                      <MenuItem key={t.key} value={t.key}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </ReactHookFormSelect>
                </Grid>
                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="Address Proof Number"
                    name="addressProofNo"
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CustomFileField
                    label={t("address_proof")}
                    name="addressProof"
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                    ref={fileInputRef}
                    accept="application/pdf,image/*"
                  />
                </Grid>

                {/* PROFILE PHOTO */}
                <Grid item xs={12} md={5}>
                  <CustomFileField
                    label={t("profile_proof")}
                    name="profileProof"
                    control={control}
                    fullWidth
                    variant="outlined"
                    errors={errors}
                    fields={validationFields}
                    ref={fileInputRef}
                    accept="application/pdf,image/*"
                  />
                </Grid>

                <Grid item xs={12} md={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    JPG / PNG • Max 2MB
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {Object.keys(cafForm).length != 0 && (
          <Grid item xs={12} md={12}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={4} key={cafForm?.addressProofPath || "address-proof"}>
                <KycPreview
                  title={"Address Proof"}
                  file={watchedAddressProof}
                  fileName={
                    watchedAddressProof?.name || cafForm.addressProofPath?.split("/").pop() || ""
                  }
                  previewUrl={images?.addressProof}
                />

                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button size="small" onClick={() => downloadKycDocument(null, null)}>
                    Download
                  </Button>

                  {approveOrRejectPermission() && isPending(null) && (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" color="success" onClick={() => approveClick(null)}>
                        Approve
                      </Button>
                      <Button size="small" color="error" onClick={() => rejectClick(null)}>
                        Reject
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} md={4} key={cafForm?.idProofPath || "id-proof"}>
                <KycPreview
                  title={"Id Proof"}
                  file={watchedIdProof}
                  fileName={watchedIdProof?.name || cafForm.idProofPath?.split("/").pop() || ""}
                  previewUrl={images?.idProof}
                />

                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button size="small" onClick={() => downloadKycDocument(null, null)}>
                    Download
                  </Button>

                  {approveOrRejectPermission() && isPending(null) && (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" color="success" onClick={() => approveClick(null)}>
                        Approve
                      </Button>
                      <Button size="small" color="error" onClick={() => rejectClick(null)}>
                        Reject
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} md={4} key={cafForm?.profileProofPath || "profile-proof"}>
                <KycPreview
                  title="Profile Photo"
                  file={watchedProfileProof}
                  fileName={
                    watchedProfileProof?.name ||
                    (cafForm.profileProofPath ? cafForm.profileProofPath.split("/").pop() : "")
                  }
                  previewUrl={images?.profileProof}
                />

                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button size="small" onClick={() => downloadKycDocument(null, null)}>
                    Download
                  </Button>

                  {approveOrRejectPermission() && isPending(null) && (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" color="success" onClick={() => approveClick(null)}>
                        Approve
                      </Button>
                      <Button size="small" color="error" onClick={() => rejectClick(null)}>
                        Reject
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Grid>
              {/* <Grid item xs={12} md={4} key={doc.kycDocumentId}>
                <KycPreview doc={doc} previewUrl={images[doc.fileName]} />

                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button
                    size="small"
                    onClick={() => downloadKycDocument(images[doc.fileName], doc.fileName)}
                  >
                    Download
                  </Button>

                  {approveOrRejectPermission() && isPending(doc.status) && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        color="success"
                        onClick={() => approveClick(doc.kycDocumentId)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => rejectClick(doc.kycDocumentId)}
                      >
                        Reject
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Grid> */}
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  }
}

export default KycDocuments;
